import { Formik } from 'formik'
import React from 'react'
import * as Yup from 'yup'
import { unwrapResult } from '@reduxjs/toolkit'

import {
  Button,
  Form,
  FormikPasswordField,
  FIELD_REQUIRED,
} from 'src/modules/ui'
import { FormActions } from 'src/modules/auth'
import { useActionRunner } from 'src/modules/notification'

import { useAuth } from '../hooks'

const ChangePasswordForm = () => {
  const { changePassword } = useAuth()
  const [doChangePassword] = useActionRunner(changePassword, {
    successNotification: 'Password changed!',
    errorNotification: result => {
      try {
        unwrapResult(result)
      } catch (err) {
        if (err.name === 'LimitExceededException') {
          return 'You have tried to login too many times - please wait a while'
        }
      }
    },
  })

  const initialValues = { oldPassword: '', newPassword: '' }

  const handleSubmit = async (
    { oldPassword, newPassword },
    { resetForm, setErrors }
  ) => {
    const result = await doChangePassword({
      oldPassword,
      newPassword,
    })
    try {
      unwrapResult(result)
      resetForm()
    } catch (error) {
      const { name, message } = error
      const fieldErrors = {}
      if (name === 'InvalidParameterException') {
        if (oldPassword.length < 6) {
          fieldErrors['oldPassword'] =
            'Password must be at least six characters long'
        }
        if (newPassword.length < 8) {
          fieldErrors['newPassword'] =
            'New passwords must be at least eight characters long'
        }
      } else if (name === 'NotAuthorizedException') {
        fieldErrors['oldPassword'] = 'Password is incorrect'
      } else if (name === 'InvalidPasswordException') {
        fieldErrors['newPassword'] = message
      }
      setErrors(fieldErrors)
    }
  }

  const validationSchema = Yup.object().shape({
    oldPassword: Yup.string()
      .required(FIELD_REQUIRED)
      .min(6, 'Must be at least six characters'),
    newPassword: Yup.string()
      .required(FIELD_REQUIRED)
      .min(8, 'Must be at least eight characters'),
  })

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ handleSubmit, isSubmitting }) => (
        <>
          <Form>
            <Form.InputGroup title="Change Password">
              <FormikPasswordField
                label="Current password"
                margin="normal"
                name="oldPassword"
                fullWidth
              />
              <FormikPasswordField
                label="New password"
                margin="normal"
                name="newPassword"
                fullWidth
              />
            </Form.InputGroup>
            <FormActions>
              <Button
                color="primary"
                onClick={handleSubmit}
                isLoading={isSubmitting}
                size="large"
                type="submit"
              >
                Update Password
              </Button>
            </FormActions>
          </Form>
        </>
      )}
    </Formik>
  )
}

export default ChangePasswordForm
